/** Per-field value transform rules on import mapping templates (opt-in per field). */

export const VALUE_MAP_MATCH_KIND_OPTIONS = [
  { value: 'equals', label: 'Equals (exact, ignore case)' },
  { value: 'contains', label: 'Contains' },
  { value: 'startsWith', label: 'Starts with' },
  { value: 'endsWith', label: 'Ends with' },
  { value: 'empty', label: 'Is empty' },
  { value: 'notEmpty', label: 'Is not empty' },
  { value: 'regex', label: 'Regex (advanced)' },
]

const VALID_MATCH_KINDS = new Set(VALUE_MAP_MATCH_KIND_OPTIONS.map((o) => o.value))
const MAX_RULES_PER_FIELD = 32

export function emptyValueMapFieldConfig() {
  return { enabled: false, rules: [], otherwise: '$raw' }
}

export function ensureValueMapFieldConfig(cfg) {
  if (!cfg || typeof cfg !== 'object') return emptyValueMapFieldConfig()
  return {
    enabled: cfg.enabled === true,
    otherwise: cfg.otherwise != null ? String(cfg.otherwise) : '$raw',
    rules: Array.isArray(cfg.rules)
      ? cfg.rules.map((r) => ({
          matchKind: VALID_MATCH_KINDS.has(String(r?.matchKind || '').trim())
            ? String(r.matchKind).trim()
            : 'equals',
          matchValue: r?.matchValue != null ? String(r.matchValue) : '',
          replaceWith: r?.replaceWith != null ? String(r.replaceWith) : '$raw',
          flags: r?.flags != null ? String(r.flags) : 'i',
        }))
      : [],
  }
}

export function ensureValueMaps(valueMaps) {
  if (!valueMaps || typeof valueMaps !== 'object') return {}
  const out = {}
  for (const [field, cfg] of Object.entries(valueMaps)) {
    out[field] = ensureValueMapFieldConfig(cfg)
  }
  return out
}

/** Persist only enabled fields with at least one rule. */
export function normalizeValueMapsForSave(valueMaps) {
  if (!valueMaps || typeof valueMaps !== 'object') return undefined
  const out = {}
  for (const [field, rawCfg] of Object.entries(valueMaps)) {
    const cfg = ensureValueMapFieldConfig(rawCfg)
    if (!cfg.enabled) continue
    const rules = cfg.rules
      .filter((r) => {
        if (r.matchKind === 'empty' || r.matchKind === 'notEmpty') return true
        return String(r.matchValue || '').trim() !== ''
      })
      .slice(0, MAX_RULES_PER_FIELD)
      .map(({ matchKind, matchValue, replaceWith, flags }) => {
        const row = { matchKind, replaceWith: replaceWith ?? '$raw' }
        if (matchKind !== 'empty' && matchKind !== 'notEmpty') row.matchValue = matchValue ?? ''
        if (matchKind === 'regex' && flags) row.flags = String(flags).slice(0, 8)
        return row
      })
    if (!rules.length) continue
    const entry = { enabled: true, rules }
    if (cfg.otherwise && cfg.otherwise !== '$raw') entry.otherwise = cfg.otherwise
    out[field] = entry
  }
  return Object.keys(out).length ? out : undefined
}

export function newValueMapRule(overrides = {}) {
  return {
    matchKind: 'equals',
    matchValue: '',
    replaceWith: '',
    flags: 'i',
    ...overrides,
  }
}
